import React from 'react'
import { Card, CardHeader, CardContent, Label, Select, Switch, Input, Button, Separator } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { getTrendConfig, setTrendConfig, getDefaults } from '@/modules/settings/services/trendConfigService'

const TF_LABEL: Record<string, string> = { '5':'5m','15':'15m','30':'30m','60':'1h','240':'4h','720':'12h','D':'1D','W':'1W','M':'1M' }
const TF_ROWS = ['15','30','60','240','720','D','W','M']
const TF_OPTIONS = ['5','15','30','60','240','720','D','W']
const TF_ORDER: Record<string, number> = { '5':1,'15':2,'30':3,'60':4,'240':5,'720':6,'D':7,'W':8,'M':9 }
const allowedOptionsFor = (target: string) => TF_OPTIONS.filter(opt => opt !== target && TF_ORDER[opt] < TF_ORDER[target])

export default function TrendConfigSettings() {
  const [formA, setFormA] = React.useState<{ bullish: Record<string,string|null>; bearish: Record<string,string|null> }>(()=>({ bullish:{}, bearish:{} }))
  const [formAEnabled, setFormAEnabled] = React.useState<boolean>(false)
  const [formBEnabled, setFormBEnabled] = React.useState<Record<string,boolean>>({})
  const [epsilon, setEpsilon] = React.useState<string>('0')
  const [retargetDom, setRetargetDom] = React.useState<boolean>(false)
  const [liveEnabled, setLiveEnabled] = React.useState<boolean>(false)
  const [livePersistence, setLivePersistence] = React.useState<Record<string,number>>({})
  const [liveEpsilonPct, setLiveEpsilonPct] = React.useState<Record<string,number>>({})
  const [loading, setLoading] = React.useState<boolean>(true)
  const [saving, setSaving] = React.useState<boolean>(false)
  const [savedAt, setSavedAt] = React.useState<number>(0)
  const { add } = useToast()

  const handleSelect = (dir: 'bullish'|'bearish', tf: string, val: string) => {
    setFormA(p => ({ ...p, [dir]: { ...p[dir], [tf]: val || null } }))
  }

  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const cfg = await getTrendConfig()
        if (!mounted) return
        setFormA({ bullish: cfg.formaA.bullish as any, bearish: cfg.formaA.bearish as any })
        setFormAEnabled(!!cfg.formaA.enabled)
        setFormBEnabled(cfg.formaB.enabled as any)
        setEpsilon(String(cfg.formaB.epsilon ?? 0))
        setRetargetDom(!!cfg.retargetDominantEnabled)
        setLiveEnabled(!!cfg.formaB.liveEnabled)
        setLivePersistence((cfg.formaB.livePersistence as any) || {})
        setLiveEpsilonPct((cfg.formaB.liveEpsilonPct as any) || {})
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => { mounted = false }
  }, [])

  const onRestoreDefaults = async () => {
    const d = getDefaults()
    setFormA({ bullish: d.formaA.bullish as any, bearish: d.formaA.bearish as any })
    setFormBEnabled(d.formaB.enabled as any)
    setEpsilon(String(d.formaB.epsilon))
    await onSave({ formaA: { bullish: d.formaA.bullish, bearish: d.formaA.bearish, enabled: formAEnabled }, formaB: d.formaB })
  }

  const onSave = async (override?: any) => {
    setSaving(true)
    try {
      const payload = override ?? {
        formaA: { bullish: formA.bullish, bearish: formA.bearish, enabled: formAEnabled },
        formaB: { enabled: formBEnabled, epsilon: Number(epsilon || '0'), liveEnabled, livePersistence, liveEpsilonPct },
        retargetDominantEnabled: retargetDom,
      }
      await setTrendConfig(payload)
      setSavedAt(Date.now())
      add({ variant: 'success', title: 'Configurações salvas', soft: true })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-medium">Configurar Tendencias</h2>
      <p className="text-sm text-foreground/70">Defina como cada timeframe confirma outro (Forma A) e se o fechamento no alvo (Forma B) fica habilitado. Valores salvos em app_settings.</p>
      {loading && <div className="text-sm text-foreground/60">Carregando...</div>}

      <Card>
        <CardHeader className="flex items-center justify-between text-base font-semibold">
          <div>Forma A - Confirmacao por pivo</div>
          <div className="flex items-center gap-2">
            <Label>Desativado</Label>
            <Switch checked={formAEnabled} onChange={e=> setFormAEnabled((e.target as HTMLInputElement).checked)} />
            <Label>Ativado</Label>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-foreground/70">Selecione qual TF menor confirma o alvo para cada timeframe, separadamente para Alta (fundo) e Baixa (topo).</p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[200px,1fr,1fr] items-center">
            <div />
            <Label>Confirma (Alta)</Label>
            <Label>Confirma (Baixa)</Label>
            <Separator className="col-span-3 my-1" />
            {TF_ROWS.map(tf => (
              <React.Fragment key={tf}>
                <div className="text-sm">{TF_LABEL[tf] || tf}</div>
                <Select value={formA.bullish[tf] || ''} disabled={!formAEnabled} onChange={e=> handleSelect('bullish', tf, (e.target as HTMLSelectElement).value)}>
                  <option value="">Selecione...</option>
                  {allowedOptionsFor(tf).map(opt => (
                    <option key={opt} value={opt}>{TF_LABEL[opt] || opt}</option>
                  ))}
                </Select>
                <Select value={formA.bearish[tf] || ''} disabled={!formAEnabled} onChange={e=> handleSelect('bearish', tf, (e.target as HTMLSelectElement).value)}>
                  <option value="">Selecione...</option>
                  {allowedOptionsFor(tf).map(opt => (
                    <option key={opt} value={opt}>{TF_LABEL[opt] || opt}</option>
                  ))}
                </Select>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button variant="secondary" onClick={onRestoreDefaults} disabled={saving}>Restaurar padrao</Button>
            <Button onClick={()=> onSave()} disabled={saving}>Salvar</Button>
            {savedAt>0 && <span className="text-xs text-foreground/60">Salvo {new Date(savedAt).toLocaleTimeString()}.</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="text-base font-semibold">Forma B - Fechamento no alvo</CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-foreground/70">Habilite por timeframe alvo e ajuste o epsilon (opcional) para o rompimento por fechamento.</p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {['60','240','720','D','W','M'].map(tf => (
              <div key={tf} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <div className="text-sm">{TF_LABEL[tf]}</div>
                <Switch checked={!!formBEnabled[tf]} onChange={e=> setFormBEnabled(p => ({ ...p, [tf]: (e.target as HTMLInputElement).checked }))} />
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-[160px,1fr] items-center gap-3 max-w-md">
            <Label>Epsilon (opcional)</Label>
            <Input value={epsilon} onChange={e=> setEpsilon((e.target as HTMLInputElement).value)} placeholder="0" />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button variant="secondary" onClick={onRestoreDefaults} disabled={saving}>Restaurar padrao</Button>
            <Button onClick={()=> onSave()} disabled={saving}>Salvar</Button>
            {savedAt>0 && <span className="text-xs text-foreground/60">Salvo {new Date(savedAt).toLocaleTimeString()}.</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between text-base font-semibold">
          <div>Retarget para TF Dominante</div>
          <div className="flex items-center gap-2">
            <Label>Desativado</Label>
            <Switch checked={retargetDom} onChange={e=> setRetargetDom((e.target as HTMLInputElement).checked)} />
            <Label>Ativado</Label>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-xs text-foreground/70">Só pós‑T0 e no sentido esperado (alta→down; baixa→up). Se desligado, mantém no TF atual.</p>
          <div className="mt-2"><Button onClick={()=> onSave()} disabled={saving}>Salvar</Button></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between text-base font-semibold">
          <div>Antecipação 5min (quase confirmada)</div>
          <div className="flex items-center gap-2">
            <Label>Desativado</Label>
            <Switch checked={liveEnabled} onChange={e=> setLiveEnabled((e.target as HTMLInputElement).checked)} />
            <Label>Ativado</Label>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-foreground/70">Conta fechamentos de 5min acima/abaixo do nível do TF‑alvo por N candles seguidos (com epsilon). Apenas avisa; quem fecha é o TF‑alvo.</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {['60','240','720','D','W','M'].map(tf => (
              <div key={tf} className="grid grid-cols-[80px,1fr,1fr] items-center gap-2 border border-border rounded-md p-2">
                <div className="text-sm">{TF_LABEL[tf]}</div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">N (5m)</Label>
                  <Input type="number" min={1} className="h-8" value={String(livePersistence[tf] ?? '')} onChange={e=> setLivePersistence(p=> ({...p, [tf]: Number((e.target as HTMLInputElement).value||0)}))} placeholder="2" />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">ε (%)</Label>
                  <Input type="number" step="0.0001" className="h-8" value={String(liveEpsilonPct[tf] ?? '')} onChange={e=> setLiveEpsilonPct(p=> ({...p, [tf]: Number((e.target as HTMLInputElement).value||0)}))} placeholder="0.1" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3"><Button onClick={()=> onSave()} disabled={saving}>Salvar</Button></div>
        </CardContent>
      </Card>
    </div>
  )
}
